import type { IEmployeeTalkState } from '@sharedInterfaces/IEmployeeTalkState';
import { useCallback, useState } from 'react';
import { useEmployeeTalkAnalysis } from './analysis';
import { useEmployeeTalkChangeDetection } from './changeDetection';
import { useComponentData } from './componentData';
import { useEmployeeTalkNavigation } from './navigation';
import { useEmployeeTalkPersonalData } from './personalData';
import { useEmployeeTalkSelectors } from './selectors';
import type { UseSecureEmployeeTalkStateReturn } from './types';
import { useEmployeeTalkWebSocket } from './websocket';

export const useSecureEmployeeTalkState = (employeeTalkId: number): UseSecureEmployeeTalkStateReturn => {
    const { talkState, loading, error, setError, dispatcherRef, isDispatcherReady } =
        useEmployeeTalkWebSocket(employeeTalkId);

    const [saving, setSaving] = useState(false);

    const { getComponentData } = useComponentData(talkState);
    const { currentStep, canNavigateToStep, navigateToStep } = useEmployeeTalkNavigation({
        talkState,
        dispatcherRef,
        isDispatcherReady,
        setError,
    });

    const updateComponentData = useCallback(
        async (componentId: string, data: unknown): Promise<void> => {
            if (!isDispatcherReady()) return;
            const dispatcher = dispatcherRef.current;
            if (!dispatcher) return;
            try {
                await dispatcher.updateComponentData(componentId, data);
            } catch (err) {
                console.error('❌ Component Update Error:', err);
                setError(err instanceof Error ? err.message : 'Unknown error');
            }
        },
        [dispatcherRef, isDispatcherReady, setError],
    );

    const { getAnalysisData, isAnalysisComplete } = useEmployeeTalkAnalysis(talkState);
    const { hasPersonalDataChanges, getPersonalDataChanges, hasDescriptionChanges } = useEmployeeTalkChangeDetection(
        talkState,
        getComponentData,
    );

    const { updatePersonalData, updateDescription, updatePersonalDataWithSaving, updateDescriptionWithSaving } =
        useEmployeeTalkPersonalData({
            dispatcherRef,
            isDispatcherReady,
            getComponentData,
            updateComponentData,
            setSaving,
            setError,
        });

    // Regeneration methods - optimized with useCallback
    const regeneratePersonalData = useCallback(
        async () => dispatcherRef.current?.regeneratePersonalData(),
        [dispatcherRef],
    );
    const regenerateDescription = useCallback(
        async () => dispatcherRef.current?.regenerateDescription(),
        [dispatcherRef],
    );
    const regenerateSkills = useCallback(async () => dispatcherRef.current?.regenerateSkills(), [dispatcherRef]);
    const regenerateCertificates = useCallback(
        async () => dispatcherRef.current?.regenerateCertificates(),
        [dispatcherRef],
    );
    const regenerateRoles = useCallback(async () => dispatcherRef.current?.regenerateRoles(), [dispatcherRef]);

    // Selection methods - optimized with useCallback
    const updateSkillLevel = useCallback(
        async (skill: string, level: number) => dispatcherRef.current?.updateSkillLevel(skill, level),
        [dispatcherRef],
    );
    const selectSkill = useCallback(
        async (skill: string, level?: number) => dispatcherRef.current?.selectSkill(skill, level),
        [dispatcherRef],
    );
    const deselectSkill = useCallback(
        async (skill: string) => dispatcherRef.current?.deselectSkill(skill),
        [dispatcherRef],
    );
    const selectCertificate = useCallback(
        async (certificate: string, start?: Date, end?: Date, duration?: number) =>
            dispatcherRef.current?.selectCertificate(certificate, start, end, duration),
        [dispatcherRef],
    );
    const deselectCertificate = useCallback(
        async (certificate: string) => dispatcherRef.current?.deselectCertificate(certificate),
        [dispatcherRef],
    );
    const selectRole = useCallback(async (role: string) => dispatcherRef.current?.selectRole(role), [dispatcherRef]);
    const deselectRole = useCallback(
        async (role: string) => dispatcherRef.current?.deselectRole(role),
        [dispatcherRef],
    );
    const selectSuperPower = useCallback(
        async (sp: string) => dispatcherRef.current?.selectSuperPower(sp),
        [dispatcherRef],
    );
    const deselectSuperPower = useCallback(
        async (sp: string) => dispatcherRef.current?.deselectSuperPower(sp),
        [dispatcherRef],
    );

    // Manual mode
    const setManualMode = useCallback(
        async (manualMode: boolean) => dispatcherRef.current?.setManualMode(manualMode),
        [dispatcherRef],
    );

    // Ignore changes
    const ignoreChanges = useCallback(
        async (componentId: string, changeType: 'personal' | 'description') =>
            dispatcherRef.current?.ignoreChanges(componentId, changeType),
        [dispatcherRef],
    );

    // Approval methods
    const approveEmployeeTalk = useCallback(
        async (role: 'employee' | 'manager') => dispatcherRef.current?.approveEmployeeTalk(role),
        [dispatcherRef],
    );
    const unapproveEmployeeTalk = useCallback(
        async (role: 'employee' | 'manager') => dispatcherRef.current?.unapproveEmployeeTalk(role),
        [dispatcherRef],
    );

    const { getSelectedSkills, getSelectedCertificates, getSelectedRoles, getSelectedSuperPowers } =
        useEmployeeTalkSelectors(talkState as IEmployeeTalkState | null);

    return {
        talkState,
        loading,
        error,
        currentStep,
        canNavigateToStep,
        getComponentData,
        updateComponentData,
        navigateToStep,
        getAnalysisData,
        isAnalysisComplete,
        isReadOnlyForCurrentUser: false,
        updatePersonalData,
        updateDescription,
        regeneratePersonalData,
        regenerateDescription,
        regenerateSkills,
        regenerateCertificates,
        regenerateRoles,
        updateSkillLevel,
        selectSkill,
        deselectSkill,
        selectCertificate,
        deselectCertificate,
        selectRole,
        deselectRole,
        selectSuperPower,
        deselectSuperPower,
        hasPersonalDataChanges,
        getPersonalDataChanges,
        hasDescriptionChanges,
        isSaving: saving,
        updatePersonalDataWithSaving,
        updateDescriptionWithSaving,
        getSelectedSkills,
        getSelectedCertificates,
        getSelectedRoles,
        getSelectedSuperPowers,
        setManualMode,
        ignoreChanges,
        approveEmployeeTalk,
        unapproveEmployeeTalk,
    };
};
